import { createSelector } from '@reduxjs/toolkit';

// Imported relatively rather than through the '@formue-app/core' barrel: the
// barrel imports ./store before ./hooks, so pulling it in from inside the store
// creates a cycle that only resolves when the graph is entered via src/index.js.
import {
  getAssetClassName,
  getPortfolioCreditQualityTagsKey,
  getPortfolioGeoTagsKey,
  getPortfolioMarketTagsKey,
} from '../../../services/utilities/portfolio';
import {
  sortByAssetClass,
  sortByAssetGroup,
  sortCreditQyality,
} from '../../../services/utilities/sorting';
import { resourceSelectors } from '../../utils/resourceSelectors';

import { operatingCountrySelector } from '../../../store/auth/selectors';
import { useNewAssetClassStructureSelector } from './configuration';

export const [portfolioAllIds, portfolioById, allPortfolioItemsSelector] =
  resourceSelectors('portfolios');

export const portfolioTotalSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    const total = items.filter((item) => item.dataType === 'total');
    return total[0];
  }
);

export const portfolioAssetGroupsSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    return items
      .filter((item) => item.dataType === 'assetGroup')
      .sort(sortByAssetGroup);
  }
);

export const portfolioAssetClassesSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    return items
      .filter((item) => item.dataType === 'assetClassTotal')
      .sort(sortByAssetClass);
  }
);

export const portfolioAssetClassesWithSubitemsSelector = createSelector(
  allPortfolioItemsSelector,
  operatingCountrySelector,
  useNewAssetClassStructureSelector,
  (items, operatingCountry, useNewAssetClassStructure) => {
    const geoItems = items.filter(
      (item) =>
        item.geo &&
        item.dataType === 'assetClass' &&
        !item.market &&
        !item.creditQuality
    );
    const marketItems = items.filter(
      (item) =>
        item.market && item.dataType === 'assetClass' && !item.creditQuality
    );
    const creditQualityItems = items
      .filter((item) => item.creditQuality && item.dataType === 'assetClass')
      .sort(sortCreditQyality);

    const products = items
      .filter((item) => item.dataType === 'product')
      .sort((a, b) => a?.product?.name?.localeCompare(b?.product?.name));

    const getFilteredProducts = (filter) => {
      return products.filter(filter).map((product) => ({
        ...product,
        ...product.product,
        name: product.product?.name,
        sold: !product.marketValue && !product.product.quantity,
        level: 5,
      }));
    };

    const getCreditQualityItems = (assetClass, geo = null, market = null) => {
      return creditQualityItems
        .filter(
          (item) =>
            item.class === assetClass &&
            (geo === null || item.geo === geo) &&
            (market === null || item.market === market)
        )
        .map((creditQualityItem) => ({
          ...creditQualityItem,
          level: 4,
          name: getPortfolioCreditQualityTagsKey(
            creditQualityItem.creditQuality,
            assetClass
          ),
          subItems: getFilteredProducts(
            (product) =>
              product.class === assetClass &&
              (geo === null || product.geo === geo) &&
              (market === null || product.market === market) &&
              product.creditQuality === creditQualityItem.creditQuality
          ),
        }));
    };

    const getMarketItems = (assetClass, geo = null) => {
      const relevantMarketItems = marketItems.filter(
        (item) =>
          item.class === assetClass && (geo === null || item.geo === geo)
      );

      return relevantMarketItems.map((marketItem) => {
        const creditQualitySubItems = getCreditQualityItems(
          assetClass,
          geo,
          marketItem.market
        );

        const subItems =
          creditQualitySubItems.length > 0
            ? creditQualitySubItems
            : getFilteredProducts(
                (product) =>
                  product.class === assetClass &&
                  (geo === null || product.geo === geo) &&
                  product.market === marketItem.market
              );

        return {
          ...marketItem,
          level: 3,
          name: getPortfolioMarketTagsKey(marketItem.market, assetClass),
          subItems,
        };
      });
    };

    const getGeoItems = (assetClass) => {
      const relevantGeoItems = geoItems.filter(
        (item) => item.class === assetClass
      );

      return relevantGeoItems.map((geoItem) => {
        const marketSubItems = getMarketItems(assetClass, geoItem.geo);
        const creditQualitySubItems = getCreditQualityItems(
          assetClass,
          geoItem.geo
        );

        const creditQualityWithoutMarket = creditQualitySubItems.filter(
          (creditItem) =>
            !marketSubItems.some((marketItem) =>
              marketItem.subItems.some(
                (subItem) => subItem.creditQuality === creditItem.creditQuality
              )
            )
        );

        const allSubItems = [...marketSubItems, ...creditQualityWithoutMarket];

        const subItems =
          allSubItems.length > 0
            ? allSubItems
            : getFilteredProducts(
                (product) =>
                  product.class === assetClass && product.geo === geoItem.geo
              );

        return {
          ...geoItem,
          level: 2,
          name: getPortfolioGeoTagsKey(
            geoItem.geo,
            assetClass,
            operatingCountry
          ),
          subItems,
        };
      });
    };

    const getAssetClassHierarchy = (assetClass) => {
      const geoSubItems = getGeoItems(assetClass);
      const marketSubItems = getMarketItems(assetClass).filter(
        (item) => !item.geo
      );
      const creditQualitySubItems = getCreditQualityItems(assetClass).filter(
        (item) => !item.geo && !item.market
      );

      const allSubItems = [
        ...geoSubItems,
        ...marketSubItems,
        ...creditQualitySubItems,
      ];

      return allSubItems.length > 0
        ? allSubItems
        : getFilteredProducts((product) => product.class === assetClass);
    };

    return items
      .filter((item) => item.dataType === 'assetClassTotal')
      .sort(sortByAssetClass)
      .map((item) => ({
        ...item,
        level: 1,
        name: getAssetClassName(
          item.class,
          undefined,
          useNewAssetClassStructure
        ),
        subItems: getAssetClassHierarchy(item.class),
      }));
  }
);

export const portfolioAssetClassesWithSubitemsSimpleSelector = createSelector(
  allPortfolioItemsSelector,
  useNewAssetClassStructureSelector,
  (items, useNewAssetClassStructure) => {
    const products = items
      .filter((item) => item.dataType === 'product')
      .sort((a, b) => a?.product?.name?.localeCompare(b?.product?.name));

    return items
      .filter((item) => item.dataType === 'assetClassTotal')
      .sort(sortByAssetClass)
      .map((item) => ({
        ...item,
        level: 1,
        name: getAssetClassName(
          item.class,
          undefined,
          useNewAssetClassStructure
        ),
        subItems: products
          .filter((subItem) => item.class === subItem.class)
          .map((product) => ({
            ...product,
            ...product.product,
            name: product.product?.name,
            sold: !product.marketValue && !product.product.quantity,
            level: 5,
          })),
      }));
  }
);

export const portfolioProductsSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    return items.filter((item) => item.dataType === 'product');
  }
);

export const createPortfolioAssetClassesSelector = (group) => {
  return createSelector(portfolioAssetClassesSelector, (items) => {
    return items.filter((item) => item.group === group);
  });
};

export const createPortfolioProductsSelector = (assetClass) => {
  return createSelector(portfolioProductsSelector, (items) => {
    return items.filter((item) => item.class === assetClass);
  });
};

export const portfolioFeeDetailsSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    return items.filter((item) => item.dataType === 'feeDetail');
  }
);

export const portfolioFeeTotalSelector = createSelector(
  allPortfolioItemsSelector,
  (items) => {
    const total = items.filter((item) => item.dataType === 'feeTotal');
    return total[0];
  }
);
